import { z } from 'zod';
import { CustomErrorParams, CustomErrorType, createExperimentalZodFormResolver, Validation } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    PortfolioPaymentPolicyAction,
    PortfolioPaymentPolicyTrigger,
    PortfolioPaymentPolicyType,
} from '@mews-commander/data-access';
import { SettlementRuleOffsetType } from '../../../../settlementRules/types';
import { CentralPaymentPolicyFormField } from './types';

const getError = ({ upperBound }: { upperBound: number }) => ({
    code: z.ZodIssueCode.custom,
    fatal: true,
    params: {
        errorType: CustomErrorType.Other,
        textKey: TextKey.ValidateNumberUpperBound,
        substitutes: { upperBound },
    } satisfies CustomErrorParams,
    path: [`${CentralPaymentPolicyFormField.Amount}.value`],
});

const errorCreatePreauthorization = getError({ upperBound: 1000 });
const errorChargeCreditCard = getError({ upperBound: 100 });

export const validationSchema = z.object({
    [CentralPaymentPolicyFormField.Name]: z.string().nullable().optional(),
    [CentralPaymentPolicyFormField.PaymentPolicyKey]: z.string().min(1),
    [CentralPaymentPolicyFormField.Trigger]: z
        .nativeEnum(PortfolioPaymentPolicyTrigger)
        .default(PortfolioPaymentPolicyTrigger.Confirmation),
    [CentralPaymentPolicyFormField.Offset]: z.object({
        days: Validation.Number.MaxNonNegativeInteger.max(365).default(0),
        hours: Validation.Number.MaxNonNegativeInteger.max(100).default(0),
        minutes: Validation.Number.MaxNonNegativeInteger.max(100).default(0),
    }),
    [CentralPaymentPolicyFormField.OffsetType]: z
        .nativeEnum(SettlementRuleOffsetType)
        .default(SettlementRuleOffsetType.After),
    [CentralPaymentPolicyFormField.Action]: z
        .nativeEnum(PortfolioPaymentPolicyAction)
        .default(PortfolioPaymentPolicyAction.ChargeCreditCard),
    [CentralPaymentPolicyFormField.Type]: z
        .nativeEnum(PortfolioPaymentPolicyType)
        .default(PortfolioPaymentPolicyType.Automatic),
    [CentralPaymentPolicyFormField.Amount]: z.discriminatedUnion('type', [
        z.object({
            type: z.literal('flat'),
            value: Validation.Number.MaxPositiveInteger,
        }),
        z.object({
            type: z.literal('percentage'),
            value: z.number().gt(0, TextKey.ValidatePositiveNumber).default(1),
        }),
    ]),
});

export const resolver = createExperimentalZodFormResolver(
    validationSchema.superRefine(({ action, amount }, ctx) => {
        if (amount.type === 'percentage') {
            if (action === PortfolioPaymentPolicyAction.ChargeCreditCard ? amount.value > 1 : amount.value > 10) {
                ctx.addIssue(
                    action === PortfolioPaymentPolicyAction.ChargeCreditCard
                        ? errorChargeCreditCard
                        : errorCreatePreauthorization
                );
            }
        }

        return z.NEVER;
    })
);

export type FormValues = z.infer<typeof validationSchema>;
